import { motion, AnimatePresence } from 'framer-motion';
import { PulsingLogo } from './PulsingLogo';
import { AnimatedDots } from './AnimatedDots';
import { useRotatingMessage } from '../../chat/draft/useRotatingMessage';

/**
 * Compact "thinking" pill: pulsing-logo on left, rotating message in middle,
 * bouncing dots on right.
 *
 * SOURCE: frontend-prod/features/research/components/ResearchThinkingLoader.tsx
 * Sized down for the 360px task pane — text rotates inside a fixed-width slot
 * so the pill doesn't reflow as messages change length.
 *
 * Used by:
 *   - Research mode while SSE-streaming a reply (replaces the static
 *     "Researching…" text in ResearchStatusPanel)
 *   - Edit mode while the agent is editing (replaces the muted banner)
 */
export interface ThinkingPillProps {
  messages: readonly string[];
  rotationIntervalMs?: number;
  className?: string;
}

export function ThinkingPill({
  messages,
  rotationIntervalMs = 2400,
  className,
}: ThinkingPillProps): JSX.Element {
  const active = useRotatingMessage(messages, rotationIntervalMs);

  return (
    <div
      className={`inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-primary/8 via-card to-primary/4 border border-border shadow-sm pl-1.5 pr-3 py-1 ${className ?? ''}`}
      aria-live="polite"
    >
      <PulsingLogo size={24} />

      <div className="relative h-4 overflow-hidden flex items-center min-w-[8rem] max-w-[12rem]">
        <AnimatePresence mode="wait">
          <motion.span
            key={active}
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -8, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="text-[12px] text-foreground inline-flex items-center whitespace-nowrap"
          >
            {active}
            <AnimatedDots />
          </motion.span>
        </AnimatePresence>
      </div>

      <div className="flex items-end gap-0.5 h-3 shrink-0">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="w-1 h-1 rounded-full bg-muted-foreground"
            animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }}
            transition={{
              repeat: Infinity,
              duration: 0.9,
              delay: i * 0.15,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>
    </div>
  );
}
